import React, {
  forwardRef,
  Suspense,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import css from 'styled-jsx/css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import ReactTooltip from 'react-tooltip';
import { useImage } from 'react-image';

import { ExitSubOrgContext } from '../../Context/ExitSubOrgContext';
import OrgsMapContext from '../../Context/OrgsMapContext';
import ToggleSidebarContext from '../../Context/ToggleSidebarContext';
import AuthService from '../../Services/AuthService';

import Can from '../../RBAC/Can';
import EditOrgModal from './EditOrgModal';
import ImageErrorBoundary from '../ImageErrorBoundary';
import {
  ASSETS,
  ASSET_MODELS,
  GEOFENCES,
  SUB_ORGS,
  USERS,
  USER_PROFILES,
} from '../../Constants/InventoryResources';
import {
  ASSET_MODEL,
  ASSET_MODEL_READ,
  CORE,
  CORE_READ,
  GEOFENCE,
  GEOFENCE_READ,
  ORGANIZATION,
  ORGANIZATION_READ,
  ORGANIZATION_UPDATE,
  USER,
  USER_READ,
  USER_PERMISSION,
  USER_PROFILE_READ,
} from '../../Constants/RBAC';
import { STICKY_OVERRIDE } from '../../Styles/Z-INDEX';

export default forwardRef(InventorySidebar);

const { className: pageLinks, styles: linkStyles } = css.resolve`
  a {
    color: var(--icon-link-gray);
  }
  a:hover {
    color: var(--text-muted);
  }
`;

const { className: tooltipClass, styles: tooltipStyles } = css.resolve`
  div {
    z-index: ${STICKY_OVERRIDE};
  }
`;

const initState = {
  assetCount: 0,
  editOrgModalOpen: false,
  error: '',
  exitOrgModalOpen: false,
  subOrgAssetCount: 0,
};

function InventorySidebar(props, ref) {
  const [state, updateState] = useState(initState);

  const exitSubOrg = useContext(ExitSubOrgContext);
  const orgsMap = useContext(OrgsMapContext);
  const toggleSidebarCollapsed = useContext(ToggleSidebarContext);

  const auth = useMemo(() => new AuthService(), []);

  const desktopView = props.width >= 992;
  const rootOrgID = auth.getUserRootOrgID();
  const userCount = props.currentOrg?.Users?.length ?? 0;

  const closeEditModal = () => {
    updateState((prevState) => ({
      ...prevState,
      editOrgModalOpen: false,
    }));
  };

  const exitOrg = () => {
    if (auth.getUserRootOrgID() === props.currentOrg?._id) return;

    const { path = '' } = props.currentOrg || {};
    const pathArray = path.split('.');
    const targetOrgID = pathArray[pathArray.length - 2];
    const { name = '' } = orgsMap?.[targetOrgID] || {};

    exitSubOrg(targetOrgID, name);
  };

  const loadCurrentOrgInfo = () => {
    if (
      props.currentOrg?.childOrgGatewayCount === state.subOrgAssetCount &&
      props.currentOrg?.gatewayCount === state.assetCount
    ) {
      return;
    }

    updateState((prevState) => ({
      ...prevState,
      assetCount: props.currentOrg?.gatewayCount,
      subOrgAssetCount: props.currentOrg?.childOrgGatewayCount,
    }));
  };

  const toggleEditModal = () => {
    updateState((prevState) => ({
      ...prevState,
      editOrgModalOpen: !prevState.editOrgModalOpen,
    }));
  };

  const updateAssetCount = (action) => {
    updateState((prevState) => ({
      ...prevState,
      assetCount:
        action === 'add' ? prevState.assetCount + 1 : prevState.assetCount - 1,
    }));
  };

  useEffect(() => {
    if (
      !props?.currentOrg?.childOrgGatewayCount &&
      !props?.currentOrg?.gatewayCount
    ) {
      return;
    }
    loadCurrentOrgInfo();
  }, [
    props?.currentOrg?.childOrgGatewayCount,
    props?.currentOrg?.gatewayCount,
  ]);

  useImperativeHandle(ref, () => ({ updateAssetCount }));

  function Fallback() {
    return (
      <FontAwesomeIcon
        className={`mx-0 ${
          props.sidebarCollapsed ? 'd-block d-lg-none' : ''
        } mx-lg-auto`}
        color="var(--brand-primary)"
        icon={faBuilding}
        size="3x"
      />
    );
  }

  function OrgLogoImage() {
    const { src } = useImage({ srcList: props.currentOrg?.logo });
    return (
      <img
        alt="organization logo"
        className={`img-fluid mx-lg-auto d-inline-block ${
          props.sidebarCollapsed && 'd-block d-lg-none'
        } ${desktopView ? 'mx-0 ' : 'ml-4'}`}
        src={src}
        style={{ maxHeight: '3rem', maxWidth: '12rem' }}
      />
    );
  }

  return (
    <div className="w-100 h-100 position-absolute">
      <div className="org-details-wrapper py-3 px-3 pb-lg-3 h-100 w-100 container d-flex flex-column align-self-center">
        <div className="mb-3 row justify-content-center pl-2">
          <ImageErrorBoundary
            color="var(--brand-primary)"
            icon={faBuilding}
            iconClassName={`mx-0 ${
              props.sidebarCollapsed ? 'd-block d-lg-none' : ''
            } mx-lg-auto`}
            iconSize="3x"
          >
            <Suspense fallback={<Fallback />}>
              {props.currentOrg?._id ? <OrgLogoImage /> : <Fallback />}
            </Suspense>
          </ImageErrorBoundary>
          <button
            className="btn shadow-none mt-2 d-inline-block d-lg-none"
            style={{ width: '50px' }}
            onClick={toggleSidebarCollapsed}
          >
            <FontAwesomeIcon
              color="var(--brand-primary)"
              icon={props.sidebarCollapsed ? faChevronDown : faChevronUp}
              size="lg"
            />
          </button>
        </div>
        <div
          className={`org-name-container row ${
            props.sidebarCollapsed && 'd-flex d-lg-none'
          }`}
        >
          <h5 className="mx-auto mb-2 mt-md-0 font-weight-light text-center">
            {props.currentOrg?.name || ''}
          </h5>
        </div>
        <div
          className={`
            ${props.sidebarCollapsed ? 'd-none' : 'd-flex flex-column'} ${
              desktopView ? 'flex-grow-1' : ''
            }
            `}
        >
          <div className="row text-center mt-0 mt-lg-2 mb-0 mb-lg-4">
            <Can
              no={(data) => (
                <div
                  className="col-4 col-md-5 offset-1 text-center"
                  data-tip={data}
                >
                  <button className="btn btn-sm btn-outline-secondary" disabled>
                    Edit
                  </button>
                </div>
              )}
              orgID={props.currentOrg?._id}
              perform={ORGANIZATION_UPDATE}
              resource={ORGANIZATION}
              yes={() => (
                <div className="col-4 col-md-5 offset-1 text-center">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={toggleEditModal}
                    disabled={!props.currentOrg?._id}
                  >
                    Edit
                  </button>
                  {state.editOrgModalOpen && (
                    <EditOrgModal
                      closeModal={closeEditModal}
                      isOpen={state.editOrgModalOpen}
                      org={props.currentOrg}
                      toggleModal={toggleEditModal}
                      updateOrg={props.updateOrg}
                    />
                  )}
                </div>
              )}
            />
            <div className="col-4 col-md-5 offset-2 offset-md-0 text-center">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={rootOrgID === props.currentOrg?._id}
                onClick={exitOrg}
              >
                Exit Org
              </button>
            </div>
          </div>

          <div className="divide-border d-none d-lg-block" />

          <div className="row text-nowrap text-center mt-2 mb-0 mb-lg-3">
            <div className="col-4 col-md-5 offset-1">
              <p>Assets</p>
              <h5 className="font-weight-normal">{state.assetCount}</h5>
            </div>
            <div className="col-4 col-md-5 offset-2 offset-md-0">
              <p>Sub Org Assets</p>
              <h5 className="font-weight-normal">{state.subOrgAssetCount}</h5>
            </div>
          </div>
          <div className="row text-nowrap text-center mb-0 mb-lg-3">
            <div className="col-4 col-md-5 offset-1">
              <p>Sub Orgs</p>
              <h5 className="font-weight-normal">
                {props.currentOrg?.childOrgCount}
              </h5>
            </div>
            <div className="col-4 col-md-5 offset-2 offset-md-0">
              <p>Users</p>
              <h5 className="font-weight-normal">{userCount}</h5>
            </div>
          </div>

          <div className="divide-border d-none d-lg-block" />

          {!props.rules ? (
            <div className="d-none d-lg-block mx-lg-n3 pt-2 flex-grow-1 outer-inventory-container">
              <div className="inner-inventory-container">
                <ResourceNavLink
                  displayText="Assets"
                  isSelected={props.selected === ASSETS}
                  orgID={props.currentOrg?._id}
                  pageLink={`/organization/${ASSETS}`}
                  perform={CORE_READ}
                  resource={CORE}
                />
                <ResourceNavLink
                  displayText="Asset Models"
                  isSelected={props.selected === ASSET_MODELS}
                  orgID={props.currentOrg?._id}
                  pageLink={`/organization/${ASSET_MODELS}`}
                  perform={ASSET_MODEL_READ}
                  resource={ASSET_MODEL}
                />
                <ResourceNavLink
                  displayText="Geofences"
                  isSelected={props.selected === GEOFENCES}
                  orgID={props.currentOrg?._id}
                  pageLink={`/organization/${GEOFENCES}`}
                  perform={GEOFENCE_READ}
                  resource={GEOFENCE}
                />
                <ResourceNavLink
                  displayText="Sub-Orgs"
                  isSelected={props.selected === SUB_ORGS}
                  orgID={props.currentOrg?._id}
                  pageLink={`/organization/${SUB_ORGS}`}
                  perform={ORGANIZATION_READ}
                  resource={ORGANIZATION}
                />
                <ResourceNavLink
                  displayText="Users"
                  isSelected={props.selected === USERS}
                  orgID={props.currentOrg?._id}
                  pageLink={`/organization/${USERS}`}
                  perform={USER_READ}
                  resource={USER}
                />
                <ResourceNavLink
                  displayText="User Profiles"
                  isSelected={props.selected === USER_PROFILES}
                  orgID={props.currentOrg?._id}
                  pageLink={`/organization/${USER_PROFILES}`}
                  perform={USER_PROFILE_READ}
                  resource={USER_PERMISSION}
                />
              </div>
            </div>
          ) : (
            <div className="flex-grow-1" />
          )}
          <div className="d-flex justify-content-center align-items-center mt-lg-0">
            <span className="text-elevat-blue">
              <span className="nine-tenth-text">powered by</span> elevāt
            </span>
          </div>
        </div>
      </div>
      <ReactTooltip className={tooltipClass} />
      {linkStyles}
      {tooltipStyles}
      <style jsx>{`
        p {
          font-size: 0.8rem;
          margin-bottom: 0rem;
          color: var(--muted-gray);
        }
        .btn,
        .btn-outline-secondary {
          cursor: pointer;
          width: 80px;
          padding: 0;
        }
        @media (min-width: 768px) {
          .page-indicator {
            height: 30px;
            width: 5px;
            left: 0;
            background-color: var(--brand-primary);
          }
          .org-info {
            margin-top: 2 rem;
          }
        }
        @media (min-width: 992px) {
          .outer-inventory-container {
            position: relative;
            height: auto;
          }
          .inner-inventory-container {
            position: absolute;
            height: 100%;
            width: 100%;
            overflow: auto;
          }
        }
      `}</style>
    </div>
  );
}

InventorySidebar.propTypes = {
  currentOrg: PropTypes.object,
  rules: PropTypes.bool,
  selected: PropTypes.string,
  sidebarCollapsed: PropTypes.bool,
  updateOrg: PropTypes.func,
  width: PropTypes.number,
};

function ResourceNavLink(props) {
  return (
    <Can
      no={() => null}
      orgID={props.orgID}
      perform={props.perform}
      resource={props.resource}
      yes={() => (
        <div className="mt-3 mb-3">
          {props.isSelected ? (
            <div className="page-indicator position-absolute" />
          ) : null}
          <Link
            to={props.pageLink}
            className={
              props.isSelected
                ? 'text-muted text-decoration-none ml-lg-5'
                : `text-decoration-none ${pageLinks} ml-lg-5`
            }
          >
            {props.displayText}
          </Link>
          <style jsx>{`
            @media (min-width: 768px) {
              .page-indicator {
                height: 30px;
                width: 5px;
                left: 0;
                background-color: var(--brand-primary);
              }
            }
          `}</style>
        </div>
      )}
    />
  );
}
ResourceNavLink.propTypes = {
  displayText: PropTypes.string,
  isSelected: PropTypes.bool,
  orgID: PropTypes.string,
  pageLink: PropTypes.string,
  perform: PropTypes.string,
  resource: PropTypes.string,
};
